export type RuntimeToolId =
  | "press_release_review"
  | "media_pitch"
  | "crisis"
  | "fact_checker"
  | "content_generator"
  | "repurpose"
  | "translate_artifact"
  | "insights_exec"
  | "seo_optimize";

export type ToolSurface =
  | "chat"
  | "content_board"
  | "content_review_modal"
  | "tools_page";

export type RuntimeToolTask =
  | "writer"
  | "editor"
  | "writer_then_editor"
  | "pitch"
  | "social"
  | "translate"
  | "insights"
  | "editor_verify"
  | "crisis";

export type RuntimeToolArtifactType =
  | "press_release"
  | "media_pitch"
  | "social_media"
  | "content";

export type RuntimeToolInputMode =
  | "brief"
  | "artifact"
  | "artifact_with_brief"
  | "conversation";

export type RuntimeToolResultKind =
  | "artifact"
  | "artifact_update"
  | "analysis"
  | "workflow";

export type RuntimeToolManifestEntry = {
  id: RuntimeToolId;
  label: string;
  description: string;
  task: RuntimeToolTask;
  surfaces: ToolSurface[];
  supportedArtifactTypes?: RuntimeToolArtifactType[];
  requiresArtifact?: boolean;
  requiresConversation?: boolean;
  inputMode: RuntimeToolInputMode;
  resultKind: RuntimeToolResultKind;
  plannerApprovedInChat: boolean;
  rendererKey?: string;
};

export const RUNTIME_TOOL_MANIFEST: RuntimeToolManifestEntry[] = [
  {
    id: "press_release_review",
    label: "Press Release Review",
    description: "Draft, review, and tighten a press release.",
    task: "writer_then_editor",
    surfaces: ["chat", "content_board", "content_review_modal", "tools_page"],
    supportedArtifactTypes: ["press_release"],
    requiresArtifact: false,
    requiresConversation: false,
    inputMode: "artifact_with_brief",
    resultKind: "artifact_update",
    plannerApprovedInChat: true,
    rendererKey: "artifact-review",
  },
  {
    id: "media_pitch",
    label: "Media Pitch",
    description: "Draft a targeted media pitch with recipients and follow-up.",
    task: "pitch",
    surfaces: ["chat", "content_board", "content_review_modal", "tools_page"],
    supportedArtifactTypes: ["media_pitch", "press_release", "content"],
    requiresArtifact: false,
    requiresConversation: false,
    inputMode: "artifact_with_brief",
    resultKind: "artifact",
    plannerApprovedInChat: true,
    rendererKey: "artifact-default",
  },
  {
    id: "crisis",
    label: "Crisis Communications",
    description: "Generate a crisis communication response pack.",
    task: "crisis",
    surfaces: ["chat", "tools_page"],
    requiresArtifact: false,
    requiresConversation: false,
    inputMode: "brief",
    resultKind: "workflow",
    plannerApprovedInChat: true,
    rendererKey: "workflow-default",
  },
  {
    id: "fact_checker",
    label: "Fact Checker",
    description: "Verify claims and highlight evidence gaps.",
    task: "editor_verify",
    surfaces: ["chat", "content_board", "content_review_modal", "tools_page"],
    supportedArtifactTypes: ["press_release", "media_pitch", "social_media", "content"],
    requiresArtifact: true,
    requiresConversation: false,
    inputMode: "artifact",
    resultKind: "analysis",
    plannerApprovedInChat: true,
    rendererKey: "analysis-default",
  },
  {
    id: "content_generator",
    label: "Content Generator",
    description: "Create a brand-aligned content draft.",
    task: "writer",
    surfaces: ["chat", "tools_page"],
    supportedArtifactTypes: ["press_release", "media_pitch", "social_media", "content"],
    requiresArtifact: false,
    requiresConversation: false,
    inputMode: "brief",
    resultKind: "artifact",
    plannerApprovedInChat: true,
    rendererKey: "artifact-default",
  },
  {
    id: "repurpose",
    label: "Repurpose",
    description: "Create channel variants from an existing artifact.",
    task: "social",
    surfaces: ["chat", "content_review_modal", "tools_page"],
    supportedArtifactTypes: ["press_release", "media_pitch", "social_media", "content"],
    requiresArtifact: true,
    requiresConversation: false,
    inputMode: "artifact_with_brief",
    resultKind: "artifact",
    plannerApprovedInChat: true,
    rendererKey: "artifact-default",
  },
  {
    id: "translate_artifact",
    label: "Translate Artifact",
    description: "Translate or localize an existing artifact while preserving its output format.",
    task: "translate",
    surfaces: ["chat", "content_review_modal", "tools_page"],
    supportedArtifactTypes: ["press_release", "media_pitch", "social_media", "content"],
    requiresArtifact: true,
    requiresConversation: false,
    inputMode: "artifact_with_brief",
    resultKind: "artifact",
    plannerApprovedInChat: true,
    rendererKey: "artifact-default",
  },
  {
    id: "insights_exec",
    label: "Insights",
    description: "Summarize themes, risks, gaps, and opportunities.",
    task: "insights",
    surfaces: ["chat", "content_review_modal", "tools_page"],
    supportedArtifactTypes: ["press_release", "media_pitch", "social_media", "content"],
    requiresArtifact: false,
    requiresConversation: false,
    inputMode: "conversation",
    resultKind: "analysis",
    plannerApprovedInChat: true,
    rendererKey: "analysis-default",
  },
  {
    id: "seo_optimize",
    label: "SEO Optimize",
    description: "Optimize content with SEO recommendations and structured metadata.",
    task: "insights",
    surfaces: ["chat", "content_review_modal", "tools_page"],
    supportedArtifactTypes: ["press_release", "content"],
    requiresArtifact: true,
    requiresConversation: false,
    inputMode: "artifact",
    resultKind: "analysis",
    plannerApprovedInChat: true,
    rendererKey: "analysis-default",
  },
];

export function getRuntimeToolManifest(toolId: RuntimeToolId): RuntimeToolManifestEntry | null {
  return RUNTIME_TOOL_MANIFEST.find((entry) => entry.id === toolId) || null;
}

export function listRuntimeToolsForSurface(
  surface: ToolSurface,
  artifactType?: RuntimeToolArtifactType | null,
): RuntimeToolManifestEntry[] {
  return RUNTIME_TOOL_MANIFEST.filter((entry) => {
    if (!entry.surfaces.includes(surface)) return false;
    if (!artifactType || !entry.supportedArtifactTypes?.length) return true;
    return entry.supportedArtifactTypes.includes(artifactType);
  });
}

export function runtimeToolSupportsSurface(
  toolId: RuntimeToolId,
  surface: ToolSurface,
  artifactType?: RuntimeToolArtifactType | null,
): boolean {
  const manifest = getRuntimeToolManifest(toolId);
  if (!manifest) return false;
  if (!manifest.surfaces.includes(surface)) return false;
  if (!artifactType || !manifest.supportedArtifactTypes?.length) return true;
  return manifest.supportedArtifactTypes.includes(artifactType);
}
